import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useNotification } from '../../contexts/NotificationContext';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import Skeleton from '../../components/Skeleton';

function VIPRequestsContent() {
  const { t } = useTranslation();
  const { showAlert } = useNotification();
  const { T, S } = useTheme();

  const [requests, setRequests] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, active: 0, revenue: 0 });
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const data = await api.getManagerPremiumMemberships(statusFilter);
      const filtered = selectedBranchId
        ? data.filter(r => String(r.branch_id) === String(selectedBranchId))
        : data;
      setRequests(filtered);

      const all = await api.getManagerPremiumMemberships();
      setStats({
        pending: (all || []).filter(m => m.status === 'pending').length,
        active: (all || []).filter(m => m.status === 'active').length,
        revenue: (all || []).filter(m => m.status === 'active').reduce((sum, m) => sum + (m.monthly_fee || 0), 0),
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.getManagerBranches().then(setBranches).catch(console.error);
  }, []);

  useEffect(() => { fetchRequests(); }, [statusFilter, selectedBranchId]);

  const handleAction = async (id, action, reason) => {
    try {
      await api.managerApproveMembership(id, action, reason);
      showAlert(action === 'approve' ? t('vip.approved', 'Membership approved') : t('vip.rejected', 'Membership rejected'));
      fetchRequests();
    } catch (err) { showAlert(err.message); }
  };

  const confirmReject = () => {
    if (rejectModal) {
      handleAction(rejectModal, 'reject', rejectReason);
      setRejectModal(null);
      setRejectReason('');
    }
  };

  const kpiCard = (label, value, color) => ({
    background: T.card,
    borderRadius: '12px',
    padding: '1.25rem 1.5rem',
    borderLeft: `4px solid ${color}`,
    flex: 1,
    minWidth: '200px',
  });

  const statusColors = {
    pending: '#f59e0b',
    active: '#22c55e',
    rejected: '#ef4444',
    expired: '#6b7280',
  };

  return (
    <AdminLayout role="manager">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={S.pageTitle}>{t('vip.requests', 'VIP Membership Requests')}</h1>
        <div style={{ width: '40px', height: '2px', background: T.orange }} />
      </div>

      {/* Branch Filter */}
      <div style={{ marginBottom: '1.5rem' }}>
        <select
          value={selectedBranchId}
          onChange={e => setSelectedBranchId(e.target.value)}
          style={{ ...S.input, maxWidth: '300px' }}
        >
          <option value="">{t('vip.allBranches', 'All Branches')}</option>
          {branches.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        <div style={kpiCard('pending', stats.pending, '#f59e0b')}>
          <div style={{ fontSize: '0.78rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem' }}>
            {t('vip.pendingRequests', 'Pending Requests')}
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#f59e0b', fontFamily: "'DM Sans', sans-serif" }}>
            {stats.pending}
          </div>
        </div>
        <div style={kpiCard('active', stats.active, '#22c55e')}>
          <div style={{ fontSize: '0.78rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem' }}>
            {t('vip.activeMembers', 'Active VIP Members')}
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#22c55e', fontFamily: "'DM Sans', sans-serif" }}>
            {stats.active}
          </div>
        </div>
        <div style={kpiCard('revenue', stats.revenue, T.orange)}>
          <div style={{ fontSize: '0.78rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem' }}>
            {t('vip.monthlyRevenue', 'Monthly Revenue')}
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 700, color: T.orange, fontFamily: "'DM Sans', sans-serif" }}>
            {stats.revenue.toLocaleString()} EGP
          </div>
        </div>
      </div>

      {/* Status Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {['pending', 'active', 'rejected', 'expired'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            style={{
              padding: '0.5rem 1.2rem',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.82rem',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              background: statusFilter === s ? statusColors[s] + '22' : 'transparent',
              color: statusFilter === s ? statusColors[s] : T.muted,
              transition: 'all 0.2s',
            }}
          >
            {t(`vip.status.${s}`, s)}
          </button>
        ))}
      </div>

      {/* Data Table */}
      {loading ? <Skeleton count={5} height="56px" /> : requests.length === 0 ? (
        <p style={{ color: T.muted }}>{t('vip.noRequests', 'No requests found')}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 0.5rem' }}>
            <thead>
              <tr>
                {[t('vip.client', 'Client'), t('vip.branch', 'Branch'), t('vip.requestedDate', 'Requested'), t('vip.status', 'Status'), t('vip.actions', 'Actions')].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '0.5rem 1rem', fontSize: '0.72rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id} style={{ background: T.card, borderRadius: '10px' }}>
                  <td style={{ padding: '0.85rem 1rem', borderRadius: '10px 0 0 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '36px', height: '36px', borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#000', fontWeight: 700, fontSize: '0.82rem',
                      }}>
                        {(r.client_name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: T.white, fontSize: '0.9rem' }}>{r.client_name}</div>
                        <div style={{ fontSize: '0.75rem', color: T.muted }}>{r.client_phone}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '0.85rem 1rem', color: T.muted, fontSize: '0.85rem' }}>{r.branch_name}</td>
                  <td style={{ padding: '0.85rem 1rem', color: T.muted, fontSize: '0.85rem' }}>
                    {r.requested_at ? new Date(r.requested_at).toLocaleDateString() : '-'}
                  </td>
                  <td style={{ padding: '0.85rem 1rem' }}>
                    <span style={{
                      display: 'inline-block', padding: '0.25rem 0.75rem', borderRadius: '20px',
                      fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
                      background: (statusColors[r.status] || '#6b7280') + '22',
                      color: statusColors[r.status] || '#6b7280',
                    }}>
                      {t(`vip.status.${r.status}`, r.status)}
                    </span>
                  </td>
                  <td style={{ padding: '0.85rem 1rem', borderRadius: '0 10px 10px 0' }}>
                    {r.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleAction(r.id, 'approve')}
                          style={{
                            padding: '0.4rem 1rem', borderRadius: '8px', border: 'none',
                            background: '#22c55e22', color: '#22c55e', cursor: 'pointer',
                            fontWeight: 600, fontSize: '0.78rem',
                          }}
                        >
                          {t('vip.approve', 'Approve')}
                        </button>
                        <button
                          onClick={() => setRejectModal(r.id)}
                          style={{
                            padding: '0.4rem 1rem', borderRadius: '8px', border: 'none',
                            background: '#ef444422', color: '#ef4444', cursor: 'pointer',
                            fontWeight: 600, fontSize: '0.78rem',
                          }}
                        >
                          {t('vip.reject', 'Reject')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setRejectModal(null)}>
          <div style={{ ...S.card, maxWidth: '420px', width: '90%', padding: '2rem' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: T.white, marginBottom: '1rem', fontFamily: "'DM Sans', sans-serif" }}>
              {t('vip.rejectReason', 'Rejection Reason')}
            </h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder={t('vip.enterReason', 'Enter reason...')}
              style={{ ...S.input, width: '100%', minHeight: '100px', resize: 'vertical', marginBottom: '1rem' }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setRejectModal(null)} style={{ ...S.btnSmall, color: T.muted }}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button onClick={confirmReject} style={{
                padding: '0.5rem 1.2rem', borderRadius: '8px', border: 'none',
                background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 600,
              }}>
                {t('vip.confirmReject', 'Confirm Reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default function VIPRequests() {
  return <ProtectedRoute requireSalonManager><VIPRequestsContent /></ProtectedRoute>;
}
